import {
    Category,
    DataComponentModel,
    Dictionary,
    ServiceComponentModel,
    DataPropertyDefinition,
    DataRelationModel,
    DataPropertyRelationModel,
    ProcessComponentModel,
    ProcessOperation,
    Scalars,
    DataTranslationMap,
    ServiceTranslationMap,
    DataPropertyTranslationMap, DictionaryToken
} from "@/store/modules/hds/Model";
import {Database, Entities, EntityMap} from "@/store/Database";
import {Module} from "vuex";
import {RootStateType} from "@/store";
import actions from "@/store/modules/hds/actions";
import mutations from "@/store/modules/hds/mutations";
import ApolloClient from "apollo-client";
import {HttpLink} from "apollo-link-http";
import {InMemoryCache} from "apollo-cache-inmemory";
import axios, {AxiosInstance} from "axios";

let nextId = 0;

export function generateTemporaryID(): Scalars['ID'] {
    return '__TEMP' + nextId++;
}

export function isTemporaryID(id: Scalars['ID']): boolean {
    return id.startsWith('__TEMP');
}

export interface HdsCollectionMap extends EntityMap {
    // 辞書
    dictionary: Dictionary;
    dictionaryToken: DictionaryToken;

    // データ構成モデル系列
    dataComponentModel: DataComponentModel;
    dataPropertyDefinition: DataPropertyDefinition;
    dataRelation: DataRelationModel;
    dataPropertyRelation: DataPropertyRelationModel;
    dataTranslationMap: DataTranslationMap;
    dataPropertyTranslationMap: DataPropertyTranslationMap;

    // サービス構成モデル系列
    serviceComponentModel: ServiceComponentModel;
    processComponentModel: ProcessComponentModel;
    processOperation: ProcessOperation;
    serviceTranslationMap: ServiceTranslationMap;

    // カテゴリ
    category: Category;
}

export type HdsDatabase = Database<HdsCollectionMap>;

export interface StateType {
    gqlClient: ApolloClient<unknown>;

    apiClient: AxiosInstance;

    db: HdsDatabase;
}

const gqlClient = new ApolloClient({
    link: new HttpLink({
        uri: process.env.VUE_APP_HDS_ENDPOINT,
        credentials: 'same-origin',
    }),
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'no-cache',
        },
        query: {
            fetchPolicy: 'no-cache',
        },
    }
});

const apiClient = axios.create({
    baseURL: process.env.VUE_APP_HDS_ENDPOINT_BASE,
    headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*'
    },
    responseType: 'json',
    // adapter: cache.adapter,
});

const db = new Database<HdsCollectionMap>('hds', ({createEntity}) => {
    // 辞書
    createEntity('dictionary', 'id');
    createEntity('dictionaryToken', 'token');

    // データ構成モデル系列
    createEntity('dataComponentModel', 'id');
    createEntity('dataPropertyDefinition', {
        primaryKey: 'id',
        indices: 'dataComponentModelId'
    });
    createEntity('dataRelation', 'id');
    createEntity('dataPropertyRelation', {
        primaryKey: 'dataRelationModelId+sourceDataPropertyDefinitionId+targetDataPropertyDefinitionId',
        indices: 'dataPropertyRelationId, dataRelationId, sourceDataPropertyDefinitionId, targetDataPropertyDefinitionId'
    });
    createEntity('dataTranslationMap', {
        primaryKey: 'id',
        uniqueKeys: 'targetDictionaryId+dataRelationModelId+parentId+sourceDataComponentModelId+targetDataComponentModelId',
        indices: 'sourceDataComponentModelId',
    })
    createEntity('dataPropertyTranslationMap', {
        primaryKey: 'id',
        uniqueKeys: 'dataTranslationMapId+sourceDataPropertyDefinitionId+targetDataPropertyDefinitionId'
    })

    // サービス構成モデル系列
    createEntity('serviceComponentModel', 'id');
    createEntity('processComponentModel', {
        primaryKey: 'id',
        indices: 'serviceComponentModelId'
    });
    createEntity('processOperation', {
        primaryKey: 'id',
        indices: 'serviceComponentModelId'
    })
    createEntity('serviceTranslationMap', {
        primaryKey: 'id',
        uniqueKeys: 'sourceServiceComponentModelId+targetServiceComponentModelId',
        indices: 'sourceServiceComponentModelId',
    });

    // カテゴリ
    createEntity('category', 'id');
});

export default {
    namespaced: true,
    state() {
        return {
            gqlClient,
            apiClient,
            db
        };
    },
    getters: {
        entities(state): Entities<HdsCollectionMap> {
            return state.db.entities;
        },
        gqlClient(state): ApolloClient<unknown> {
            return state.gqlClient;
        },
        apiClient(state): AxiosInstance {
            return state.apiClient;
        },
        db(state): HdsDatabase {
            return state.db;
        }
    },
    actions,
    mutations
} as Module<StateType, RootStateType>;
